// CP204 / CP204A due-date logic (LHDN).
// Given a company's Financial Year End (accounting period end, "YYYY-MM-DD") and a Year of
// Assessment, work out the basis period and the statutory estimate / revision deadlines:
//   - CP204 (original estimate): at least 30 days BEFORE the basis period begins.
//   - CP204A 1st revision: in the 6th month of the basis period.
//   - CP204A 2nd revision: in the 9th month of the basis period.
//   - CP204A 3rd revision: in the 11th month of the basis period.
// (Matches the worked example: FYE 30 April, YA2027 -> original 1 Apr 2026, 1st Oct 2026,
//  2nd Jan 2027, 3rd Mar 2027.)

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Display helpers: NO year. Exact dates show "DD Mon"; month-based deadlines (CP204A revisions)
// show the LAST DAY of that month + month, e.g. "30 Jun".
const fmtDate = (d) => `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}`;
const fmtMonth = (d) => {
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return `${lastDay} ${MONTHS[d.getMonth()]}`;
};

const parseFye = (fyeStr) => {
  if (!fyeStr) return null;
  const parts = String(fyeStr).slice(0, 10).split("-");
  if (parts.length < 3) return null;
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (isNaN(month) || isNaN(day)) return null;
  return { month, day };
};

export const cp204Schedule = (fyeStr, ya) => {
  const p = parseFye(fyeStr);
  if (!p) return null;
  const y = Number(ya);
  if (!y) return null;
  // Basis period ENDS on the financial year end falling in this YA; it begins the day after the
  // previous year's financial year end.
  const basisStart = new Date(y - 1, p.month, p.day);
  basisStart.setDate(basisStart.getDate() + 1);
  const basisEnd = new Date(y, p.month, p.day);
  const original = new Date(basisStart);
  original.setMonth(original.getMonth() - 1);
  original.setDate(0);
  const rev = (n) => {
    const r = new Date(basisStart);
    r.setMonth(r.getMonth() + (n - 1));
    return r;
  };
  return {
    basisStart: fmtDate(basisStart),
    basisEnd: fmtDate(basisEnd),
    original: fmtDate(original),
    rev1: fmtMonth(rev(6)),
    rev2: fmtMonth(rev(9)),
    rev3: fmtMonth(rev(11)),
  };
};

// Auto schedule (no Year of Assessment): works out the UPCOMING basis period purely from the
// financial year end's month + day, relative to "today". Finds the next statutory deadline
// (original estimate or a CP204A revision) that is still due, and returns that basis period's
// full set of dates plus how soon the next deadline falls.
export const cp204Auto = (fyeStr, now = new Date()) => {
  const p = parseFye(fyeStr);
  if (!p) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const y0 = now.getFullYear();
  let best = null;
  for (let y = y0 - 1; y <= y0 + 2; y++) {
    const basisEnd = new Date(y, p.month, p.day);
    const basisStart = new Date(y - 1, p.month, p.day);
    basisStart.setDate(basisStart.getDate() + 1);
    // CP204 original estimate = last day of the month ONE month before the financial year end.
    const original = new Date(basisStart);
    original.setMonth(original.getMonth() - 1);
    original.setDate(0);
    const revMonth = (n) => { const r = new Date(basisStart); r.setMonth(r.getMonth() + (n - 1)); return r; };
    // Revision window closes at the END of the 6th/9th/11th month -> use that as the deadline.
    const revEnd = (n) => { const r = new Date(basisStart); r.setMonth(r.getMonth() + n); r.setDate(0); return r; };
    const deadlines = [
      { label: "CP204", date: original },
      { label: "CP204A (1st)", date: revEnd(6) },
      { label: "CP204A (2nd)", date: revEnd(9) },
      { label: "CP204A (3rd)", date: revEnd(11) },
    ];
    for (const d of deadlines) {
      if (d.date >= today && (!best || d.date < best.date)) {
        best = {
          date: d.date, label: d.label, basisEnd,
          original, rev1: revMonth(6), rev2: revMonth(9), rev3: revMonth(11),
        };
      }
    }
  }
  if (!best) return null;
  const days = Math.round((best.date - today) / 86400000);
  return {
    nextLabel: best.label,
    nextDate: fmtDate(best.date),
    daysToNext: days,
    dueSoon: days <= 30,
    basisEnd: fmtDate(best.basisEnd),
    original: fmtDate(best.original),
    rev1: fmtMonth(best.rev1),
    rev2: fmtMonth(best.rev2),
    rev3: fmtMonth(best.rev3),
  };
};

// "YYYY-MM-DD" -> "30 Apr" (day + month only, for a compact FYE label).
export const fyeLabel = (fyeStr) => {
  const p = parseFye(fyeStr);
  if (!p) return "";
  return `${String(p.day).padStart(2, "0")} ${MONTHS[p.month]}`;
};
